import { useState, Fragment } from 'react';
import { Box, Text, Group, Badge, Stack, ActionIcon } from '@mantine/core';
import { format, startOfWeek, parseISO } from 'date-fns';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { ParticipantAvatar } from '../../../../components/common/ParticipantAvatar';
import { CoachScheduleCell } from './CoachScheduleCell';
import { CoachScheduleSlotCell } from './CoachScheduleSlotCell';
import type {
  ScheduleGridSession,
  ScheduleGridCoach,
  ScheduleGridTimeSlot,
} from '../../../../services/api/cohortCoaches';

const MODALITY_DISPLAY: Record<string, { label: string; color: string; variant: 'filled' | 'outline' | 'light' }> = {
  group_coaching: { label: 'GROUP', color: 'medleyGreen', variant: 'light' },
  one_on_one: { label: '1:1', color: 'medleyGreen', variant: 'light' },
  workshop: { label: 'WORKSHOP', color: 'medleyGreen', variant: 'light' },
};

const DAY_ABBREV: Record<string, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
};

interface CoachScheduleGridProps {
  sessions: ScheduleGridSession[];
  coaches: ScheduleGridCoach[];
  timeSlots: ScheduleGridTimeSlot[];
  onSessionClick?: (sessionId: string) => void;
  onCoachClick?: (coachUserId: string) => void;
}

function getCoachModalities(coach: ScheduleGridCoach): string[] {
  return coach.modality_proficiencies || [];
}

function formatTimeSlotLabel(slot: ScheduleGridTimeSlot): string {
  const day = DAY_ABBREV[slot.day_of_week] || slot.day_of_week;
  return `${day} ${slot.display_time} ${slot.timezone_abbreviation}`;
}

export function CoachScheduleGrid({
  sessions,
  coaches,
  timeSlots,
  onSessionClick,
  onCoachClick,
}: CoachScheduleGridProps) {
  const columnCount = sessions.length;
  const [expandedCoaches, setExpandedCoaches] = useState<Set<string>>(new Set());

  // Sort coaches by first name to match the assignments table order
  const sortedCoaches = [...coaches].sort((a, b) =>
    (a.first_name || '').localeCompare(b.first_name || ''),
  );

  const toggleExpanded = (coachId: string) => {
    setExpandedCoaches(prev => {
      const next = new Set(prev);
      if (next.has(coachId)) {
        next.delete(coachId);
      } else {
        next.add(coachId);
      }
      return next;
    });
  };

  return (
    <Box
      style={{
        overflowX: 'auto',
        border: '1px solid #E8E6E1',
        borderRadius: 8,
      }}
    >
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: `312px repeat(${columnCount}, minmax(140px, 1fr))`,
          minWidth: 312 + columnCount * 140,
        }}
      >
        {/* Header Row */}
        <Box
          style={{
            position: 'sticky',
            left: 0,
            zIndex: 2,
            backgroundColor: '#F9F8F6',
            borderBottom: '1px solid #E8E6E1',
            borderRight: '1px solid #E8E6E1',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'flex-start',
          }}
        >
          <Text fz={12} fw={600} c="var(--mantine-color-gray-7)" tt="uppercase">
            COACH
          </Text>
        </Box>

        {sessions.map((session) => (
          <Box
            key={session.session_number}
            style={{
              backgroundColor: '#F9F8F6',
              borderBottom: '1px solid #E8E6E1',
              borderRight: '1px solid #E8E6E1',
              padding: '8px 12px',
            }}
          >
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                Session {session.session_number}
                {session.topic_name && (
                  <Text span size="sm" fw={400} fs="italic" c="dimmed">
                    {' — '}{session.topic_name}
                  </Text>
                )}
              </Text>
              {session.scheduled_date && (
                <Text size="xs" c="dimmed">
                  Week of {format(startOfWeek(parseISO(session.scheduled_date), { weekStartsOn: 1 }), 'MMM d')}
                </Text>
              )}
            </Stack>
          </Box>
        ))}

        {/* Coach Rows */}
        {sortedCoaches.map((coach) => {
          const modalities = getCoachModalities(coach);
          const fullName = [coach.first_name, coach.last_name].filter(Boolean).join(' ') || coach.email;
          const isExpanded = expandedCoaches.has(coach.user_id);

          return (
            <Fragment key={coach.user_id}>
              {/* Summary Row */}
              <Box style={{ display: 'contents' }}>
                {/* Coach Info (sticky left) */}
                <Box
                  style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    backgroundColor: '#FFFFFF',
                    borderBottom: isExpanded ? 'none' : '1px solid #E8E6E1',
                    borderRight: '1px solid #E8E6E1',
                    padding: '10px 8px 10px 4px',
                  }}
                >
                  <Group gap={4} wrap="nowrap">
                    {/* Expand/collapse chevron */}
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="lg"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(coach.user_id);
                      }}
                      aria-label={isExpanded ? 'Collapse time slots' : 'Expand time slots'}
                    >
                      {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                    </ActionIcon>

                    <Group
                      gap="sm"
                      wrap="nowrap"
                      style={{ cursor: onCoachClick ? 'pointer' : 'default', minWidth: 0, flex: 1 }}
                      onClick={() => onCoachClick?.(coach.user_id)}
                    >
                      <ParticipantAvatar
                        firstName={coach.first_name || ''}
                        lastName={coach.last_name || ''}
                        size={40}
                      />
                      <Stack gap={2} style={{ minWidth: 0 }}>
                        <Text size="md" fw={500} lineClamp={1}>
                          {fullName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {coach.group_count} group{coach.group_count !== 1 ? 's' : ''} assigned
                        </Text>
                        {modalities.length > 0 && (
                          <Group gap={4}>
                            {modalities.map((m) => {
                              const display = MODALITY_DISPLAY[m];
                              if (!display) return null;
                              return (
                                <Badge
                                  key={m}
                                  size="xs"
                                  color={display.color}
                                  variant={display.variant}
                                  style={{ fontSize: 10, padding: '1px 6px' }}
                                >
                                  {display.label}
                                </Badge>
                              );
                            })}
                          </Group>
                        )}
                      </Stack>
                    </Group>
                  </Group>
                </Box>

                {/* Session Cells */}
                {sessions.map((session) => {
                  const cell = coach.cells[session.session_number] || {
                    state: 'not_requested' as const,
                  };
                  return (
                    <Box
                      key={session.session_number}
                      style={{
                        borderBottom: isExpanded ? 'none' : '1px solid #E8E6E1',
                        borderRight: '1px solid #E8E6E1',
                        padding: 4,
                        backgroundColor: '#FFFFFF',
                      }}
                    >
                      <CoachScheduleCell
                        cell={cell}
                        sessionNumber={session.session_number}
                        onSessionClick={onSessionClick}
                      />
                    </Box>
                  );
                })}
              </Box>

              {/* Expanded Time Slot Rows */}
              {isExpanded && timeSlots.map((slot, slotIdx) => {
                const isLastSlot = slotIdx === timeSlots.length - 1;
                return (
                  <Box key={slot.id} style={{ display: 'contents' }}>
                    {/* Time slot label (sticky left, indented) */}
                    <Box
                      style={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        backgroundColor: '#FAFAF8',
                        borderBottom: isLastSlot ? '1px solid #E8E6E1' : '1px solid #F0EFEC',
                        borderRight: '1px solid #E8E6E1',
                        padding: '6px 12px 6px 40px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Text size="sm" c="dimmed" fw={400}>
                        {formatTimeSlotLabel(slot)}
                      </Text>
                    </Box>

                    {/* Per-slot cells */}
                    {sessions.map((session) => {
                      const cell = coach.cells[session.session_number];
                      const slotDetail = cell?.slot_details?.find(
                        sd => sd.time_slot_id === slot.id,
                      );
                      return (
                        <Box
                          key={session.session_number}
                          style={{
                            borderBottom: isLastSlot ? '1px solid #E8E6E1' : '1px solid #F0EFEC',
                            borderRight: '1px solid #E8E6E1',
                            padding: 3,
                            backgroundColor: '#FAFAF8',
                          }}
                        >
                          {slotDetail ? (
                            <CoachScheduleSlotCell slotCell={slotDetail} />
                          ) : (
                            <Box style={{ minHeight: 36 }} />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
